import React, { useEffect, useState } from 'react';
import axios from '../../api/axios';
import { getSiteDisplaySettingsRoute } from '../../api/routes';
import { getLongDateString, getDefaultTimeZoneListData } from '../../helpers/Dates';
import { getTimezoneOptions } from '../../helpers/TimeZones';
import config from '../../config';
import TimezoneSelector from '../TimezoneSelector/TimezoneSelector';
import './PageTopBanner.scss';

const toDisplayUrl = (url) => (url && url.startsWith('/') ? `${config.apiBaseUrl}${url}` : (url || ''));

const HIDE_COOKIE = 'hideInfoBar';

const getHideCookieTs = () => {
    const match = document.cookie.match(/(?:^|; )hideInfoBar=([^;]*)/);
    return match ? parseInt(decodeURIComponent(match[1]), 10) : null;
};

const setHideCookie = () => {
    const val = encodeURIComponent(String(Date.now()));
    document.cookie = `${HIDE_COOKIE}=${val}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;
};

const formatDateValue = (ms) => {
    if (!ms) return '';
    try { return getLongDateString(Math.floor(ms)); }
    catch { return ''; }
};

/**
 * Format UTC seconds-from-midnight into a 12-hour time string adjusted for
 * the given timezone offset (hours, may be fractional).
 */
const formatTimeValue = (utcSecs, tzOffset = 0) => {
    if (utcSecs === undefined || utcSecs === null) return '';
    const localSecs = utcSecs + tzOffset * 3600;
    const normalised = ((localSecs % 86400) + 86400) % 86400;
    const h = Math.floor(normalised / 3600);
    const m = Math.floor((normalised % 3600) / 60);
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

const PageTopBanner = () => {
    const [settings, setSettings] = useState(null);
    const [selectedTimezone, setSelectedTimezone] = useState(null);
    const [infoBarHidden, setInfoBarHidden] = useState(false);

    useEffect(() => {
        const opts = getTimezoneOptions();
        const defTz = getDefaultTimeZoneListData(opts);
        if (defTz) setSelectedTimezone(defTz);
    }, []);

    useEffect(() => {
        axios.get(getSiteDisplaySettingsRoute, { withCredentials: true })
            .then(res => {
                if (res.data.success) {
                    setSettings(res.data);
                    // Show bar again if infoBar was updated after the user dismissed it
                    const cookieTs = getHideCookieTs();
                    if (cookieTs !== null) {
                        const updatedAt = res.data.infoBarUpdatedAt
                            ? new Date(res.data.infoBarUpdatedAt).getTime()
                            : 0;
                        if (cookieTs > updatedAt) setInfoBarHidden(true);
                    }
                }
            })
            .catch(() => {});
    }, []);

    if (!settings) return null;

    const handleCloseInfoBar = () => {
        setHideCookie();
        setInfoBarHidden(true);
    };

    const { infoBar, banner } = settings;
    const tzOffset = selectedTimezone?.offset ?? 0;

    const renderMessage = (parts) => (parts || []).map((p, i) => {
        if (p.type === 'date') return <span key={i} className="ptb-part-date">{formatDateValue(p.dateValue)}</span>;
        if (p.type === 'time') return (
            <span key={i} className="ptb-part-time">
                {formatTimeValue(p.timeValue, tzOffset)}
            </span>
        );
        return <span key={i}>{p.content}</span>;
    });

    return (
        <>
            {infoBar?.isVisible && !infoBarHidden && (
                <div className="page-top-message-bar">
                    {infoBar.showLozenge && (
                        <span
                            className="page-top-lozenge"
                            style={{ backgroundColor: infoBar.lozengeColor, color: infoBar.lozengeTextColor || '#fff' }}
                        >
                            {infoBar.lozengeText}
                        </span>
                    )}
                    <span className="ptb-message">{renderMessage(infoBar.messageParts)}</span>
                    {infoBar.showRegionDropdown && (
                        <TimezoneSelector
                            value={selectedTimezone}
                            onChange={setSelectedTimezone}
                            className="ptb-region"
                        />
                    )}
                    <button
                        className="ptb-close"
                        onClick={handleCloseInfoBar}
                        type="button"
                        aria-label="Dismiss info bar"
                    >✕</button>
                </div>
            )}
            {banner?.isVisible && (
                <div
                    className="page-top-banner"
                    style={{ backgroundColor: banner.backgroundColor }}
                >
                    {banner.imageUrl && (
                        <img
                            src={toDisplayUrl(banner.imageUrl)}
                            alt="Banner"
                            style={{ objectFit: banner.imageObjectFit || 'cover' }}
                        />
                    )}
                </div>
            )}
        </>
    );
};

export default PageTopBanner;
